import { Maximize, Minimize } from 'lucide-react';
import { FC, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { IPsySetting } from '../../../store/psyTools.slice';
import { shuffleArray } from '../../../utils/textHelpers';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { clearMacClientSelections, clearMacSharedView } from '../../../store/mac.slice';

import { MacInfoIcon } from '../../icons/mac-info-icon';
import { ShuffleIcon } from '../../icons/shuffle';
import { Button, CloseButton, Heading } from '../../../shared/ui';

import { DeckPreview } from './mac-card-psy';
import { MacDeckGalleryModal } from './mac-deck-gallery-modal';

const MAC_DECK_TOOLTIP_TEXT =
  'Выберите одну колоду из 25 для показа клиенту и способ ее отображения: рубашкой вверх или вниз';

export interface MacDeckSelectContentProps {
  onClose: () => void;
  onConfirm?: () => void;
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
  className?: string;
  variant?: 'modal' | 'panel';
  /** Для variant="panel" на светлом фоне: тёмный текст и кнопка закрытия */
  panelLightTheme?: boolean;
  /** Панель развёрнута на всю высоту (только variant="panel") */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние (только variant="panel") */
  onExpandToggle?: () => void;
}

/**
 * Контент выбора колоды МАК без обёртки модалки.
 * Используется в MacDeckSelectModal (и при необходимости в панели групповой терапии).
 */
export const MacDeckSelectContent: FC<MacDeckSelectContentProps> = ({
  onClose,
  onConfirm,
  sendMacCardsOpen,
  className,
  variant = 'modal',
  panelLightTheme = false,
  isExpanded = false,
  onExpandToggle,
}) => {
  const dispatch = useAppDispatch();
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const cards = psyTools.find((el) => el.name === 'metaforic_cards')?.psy_tool_settings || [];

  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [coverDown, setCoverDown] = useState(false);
  const [inspectedDeck, setInspectedDeck] = useState<IPsySetting | null>(null);
  const [deckTooltipVisible, setDeckTooltipVisible] = useState(false);
  const [deckTooltipRect, setDeckTooltipRect] = useState<{ top: number; right: number } | null>(null);
  const deckInfoIconRef = useRef<HTMLDivElement>(null);

  const showDeckTooltip = () => {
    const rect = deckInfoIconRef.current?.getBoundingClientRect();
    if (rect) {
      setDeckTooltipRect({ top: rect.top, right: rect.right });
      setDeckTooltipVisible(true);
    }
  };
  const hideDeckTooltip = () => {
    setDeckTooltipVisible(false);
    setDeckTooltipRect(null);
  };

  const handleShowDeck = () => {
    if (!selectedCard) return;

    const selectedSettings = cards.find((el) => el.id === selectedCard)?.settings?.[0]?.value || [];
    const cover = selectedSettings[0] || '';
    const color = selectedSettings[1] || '';
    const selectedDeck = cards.find((el) => el.id === selectedCard);
    const deck = selectedDeck?.value || [];
    const payload = {
      show: coverDown,
      cover,
      color,
      result: {
        name: selectedDeck?.name || '',
        value: shuffleArray(Object.values(deck)),
      },
    };

    dispatch(clearMacClientSelections());
    dispatch(clearMacSharedView());
    sendMacCardsOpen?.(payload);
    dispatch(setActiveTool(PsyToolsId.MAC_CARDS));
    onConfirm?.();
    if (variant !== 'panel') {
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedCard(null);
    setInspectedDeck(null);
    onClose();
  };

  const isPanel = variant === 'panel';
  const isPanelLight = isPanel && panelLightTheme;

  return (
    <>
      <div
        className={classNames(
          'relative flex flex-col h-full min-h-0 overflow-hidden',
          isPanel ? 'gap-3 px-4 py-4' : 'gap-4',
          className,
        )}
      >
        {isPanel && (
          <div className="absolute top-5 right-5 flex items-center gap-2 z-[40] pointer-events-auto">
            {onExpandToggle && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="pointer-events-auto"
                aria-label={isExpanded ? 'Свернуть панель' : 'Расширить панель'}
                startIcon={isExpanded ? <Minimize size={16} aria-hidden /> : <Maximize size={16} aria-hidden />}
                onClick={onExpandToggle}
              >
                {isExpanded ? 'Свернуть' : 'Расширить'}
              </Button>
            )}
            <CloseButton className="pointer-events-auto" onClick={handleClose} />
          </div>
        )}
        {isPanel && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              ref={deckInfoIconRef}
              className="shrink-0"
              onMouseEnter={showDeckTooltip}
              onMouseLeave={hideDeckTooltip}
            >
              <MacInfoIcon width={16} height={16} className="cursor-default" />
            </div>
            <Heading size="sm" as="h2">
              МАК колоды
            </Heading>
          </div>
        )}
        {isPanel &&
          deckTooltipVisible &&
          deckTooltipRect &&
          createPortal(
            <div
              className="fixed z-popover w-[207px] rounded-control px-3 py-2.5 text-ui text-white shadow-popup"
              style={{
                backgroundColor: '#2C3038',
                left: deckTooltipRect.right + 8,
                top: deckTooltipRect.top - 80,
              }}
              onMouseEnter={showDeckTooltip}
              onMouseLeave={hideDeckTooltip}
            >
              <p className="leading-snug">{MAC_DECK_TOOLTIP_TEXT}</p>
              <div
                className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent"
                style={{ borderRightColor: '#2C3038' }}
              />
            </div>,
            document.body,
          )}

        <div className={classNames('flex-1 min-h-0', isPanel && 'relative flex flex-col')}>
          <div
            className={classNames(
              'grid grid-cols-2 overflow-y-auto white-scroll',
              isPanel
                ? 'flex-1 min-h-0 gap-3 pr-1 w-full min-w-0 lg:grid-cols-6 pb-[100px]'
                : 'max-h-[60vh] gap-3 pr-1 lg:grid-cols-4',
            )}
          >
            {cards.map((el, idx) => (
              <DeckPreview
                key={el?.id ?? idx}
                deck={el}
                coverDown={coverDown}
                isSelected={selectedCard === el?.id}
                onSelect={(id) => setSelectedCard(id)}
                onInspect={(deck) => setInspectedDeck(deck)}
              />
            ))}
          </div>

          {isPanel ? (
            <div className="absolute z-10 bottom-4 left-0 right-0 flex justify-center items-center px-4 py-3 bg-white/70 backdrop-blur-md pointer-events-none">
              <div className="flex items-center gap-[20px] pointer-events-auto">
                <Button
                  size="md"
                  variant="neutral"
                  iconOnly
                  aria-label="Карты рубашкой вверх"
                  pressed={coverDown}
                  onClick={() => setCoverDown(!coverDown)}
                >
                  <ShuffleIcon />
                </Button>
                <Button size="md" disabled={!selectedCard} onClick={handleShowDeck}>
                  Показать колоду
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-[20px] pt-2 flex-shrink-0">
              <Button
                size="md"
                variant="neutral"
                iconOnly
                aria-label="Карты рубашкой вверх"
                pressed={coverDown}
                onClick={() => setCoverDown(!coverDown)}
              >
                <ShuffleIcon />
              </Button>
              <Button size="md" fullWidth disabled={!selectedCard} onClick={handleShowDeck}>
                Показать колоду
              </Button>
            </div>
          )}
        </div>
      </div>

      <MacDeckGalleryModal
        isOpen={!!inspectedDeck}
        deckName={inspectedDeck?.name}
        cards={Array.isArray(inspectedDeck?.value) ? (inspectedDeck?.value as string[]) : []}
        onClose={() => setInspectedDeck(null)}
      />
    </>
  );
};
